/**
 * Displays CAM reconciliation source data — expense pools with line items
 * and per-tenant reconciliation with reimbursement structure details.
 */

import { useState } from "react";
import { ChevronDown, ChevronRight, Eye } from "lucide-react";
import type { SourceAuditCamRec } from "../../api/client";
import { fmtCurrency, fmtPercent, fmtSF, fmtPSF } from "../../utils/format";

interface Props {
  camRec: SourceAuditCamRec;
  isPdf: boolean;
  onViewSource: (tenantName: string) => void;
}

export default function CamRecSourceCard({ camRec, isPdf, onViewSource }: Props) {
  const [showPools, setShowPools] = useState(true);
  const [expandedPool, setExpandedPool] = useState<string | null>(null);

  return (
    <div className="border-t border-grove-200">
      {/* Summary header */}
      <div className="px-5 py-3 bg-grove-50 border-b border-grove-200">
        <div className="flex items-center gap-4 text-xs text-bark-500">
          {camRec.reconciliation_year && <span>Year: <strong className="text-bark-700">{camRec.reconciliation_year}</strong></span>}
          {camRec.total_leasable_area > 0 && <span>GLA: <strong className="text-bark-700">{fmtSF(camRec.total_leasable_area)}</strong></span>}
          {camRec.gross_up_factor > 0 && <span>Gross-up: <strong className="text-bark-700">{fmtPercent(camRec.gross_up_factor)}</strong></span>}
        </div>
      </div>

      {/* Expense pools */}
      {camRec.expense_pools.length > 0 && (
        <div className="border-b border-grove-200">
          <button
            onClick={() => setShowPools(!showPools)}
            className="w-full flex items-center gap-2 px-5 py-2.5 text-left text-xs font-semibold text-bark-600 uppercase tracking-wider hover:bg-grove-50"
          >
            {showPools ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            Expense Pools ({camRec.expense_pools.length})
          </button>
          {showPools && (
            <div className="px-5 pb-3 space-y-2">
              {camRec.expense_pools.map((pool, poolIdx) => (
                <div key={`${pool.pool_type}-${poolIdx}`} className="border border-grove-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setExpandedPool(expandedPool === pool.pool_type ? null : pool.pool_type)}
                    className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-grove-50"
                  >
                    <span className="font-medium text-bark-700">{pool.pool_name || pool.pool_type}</span>
                    <div className="flex items-center gap-3 text-xs text-bark-500">
                      <span>Actual: <strong className="text-bark-700">{fmtCurrency(pool.total_amount)}</strong></span>
                      {pool.budgeted_amount > 0 && <span>Budget: {fmtCurrency(pool.budgeted_amount)}</span>}
                      {pool.admin_fee_pct > 0 && <span>Admin: {fmtPercent(pool.admin_fee_pct)}</span>}
                      {pool.line_items.length > 0 && (
                        expandedPool === pool.pool_type
                          ? <ChevronDown className="w-3 h-3" />
                          : <ChevronRight className="w-3 h-3" />
                      )}
                    </div>
                  </button>
                  {expandedPool === pool.pool_type && pool.line_items.length > 0 && (
                    <div className="border-t border-grove-200 bg-grove-50 px-3 py-2">
                      <table className="w-full text-xs">
                        <tbody>
                          {pool.line_items.map((li, i) => (
                            <tr key={i} className="border-b border-grove-100 last:border-0">
                              <td className="py-1 text-bark-600">{li.name}</td>
                              <td className="py-1 text-right tabular-nums text-bark-700">{fmtCurrency(li.amount)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Tenant reconciliation table */}
      {camRec.tenants.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-grove-50 text-bark-500">
                <th className="text-left px-4 py-2 text-xs font-medium">Tenant</th>
                <th className="text-right px-3 py-2 text-xs font-medium">SF</th>
                <th className="text-right px-3 py-2 text-xs font-medium">Pro Rata</th>
                <th className="text-left px-3 py-2 text-xs font-medium">Structure</th>
                <th className="text-right px-3 py-2 text-xs font-medium">CAM</th>
                <th className="text-right px-3 py-2 text-xs font-medium">Tax</th>
                <th className="text-right px-3 py-2 text-xs font-medium">Insurance</th>
                <th className="text-right px-3 py-2 text-xs font-medium">Total</th>
                {isPdf && <th className="text-center px-3 py-2 text-xs font-medium">Source</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-grove-200">
              {camRec.tenants.map((t) => (
                <tr key={t.tenant_name} className="hover:bg-grove-50 transition-colors">
                  <td className="px-4 py-2 font-medium text-bark-800">
                    {t.tenant_name}
                    {t.has_exclusions && t.exclusion_categories.length > 0 && (
                      <span className="ml-1.5 text-[10px] text-bark-400">
                        (excl: {t.exclusion_categories.join(", ")})
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-bark-600">{fmtSF(t.leased_sf)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-bark-600">{fmtPercent(t.pro_rata_share)}</td>
                  <td className="px-3 py-2 text-bark-600">
                    <span className="text-xs">{t.lease_class || "—"}</span>
                    {t.stop_amount_psf > 0 && <span className="text-[10px] text-bark-400 ml-1">stop: {fmtPSF(t.stop_amount_psf)}</span>}
                    {t.expense_cap_pct != null && t.expense_cap_pct > 0 && <span className="text-[10px] text-bark-400 ml-1">cap: {fmtPercent(t.expense_cap_pct)}</span>}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-bark-600">{fmtCurrency(t.cam_actual)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-bark-600">{fmtCurrency(t.tax_actual)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-bark-600">{fmtCurrency(t.insurance_actual)}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-medium text-bark-800">{fmtCurrency(t.total_actual)}</td>
                  {isPdf && (
                    <td className="px-3 py-2 text-center">
                      <button
                        onClick={() => onViewSource(t.tenant_name)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs text-forest-700 hover:bg-forest-50 rounded-md transition-colors"
                      >
                        <Eye className="w-3 h-3" /> View
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
